"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { DataBuilderFormulaType, schemaWithTitle } from '@membranehq/sdk';
import { EditorTagBadge, EditorTagBadgeIcon } from '../../ComboBox/Badges/EditorTagBadge/index.mjs';
import { SvgIconType } from '../../SvgIcon/svg-icon-type.mjs';
import { withSlotScopeClasses } from '../../../helpers/scoped-slots.mjs';
import { forwardRef } from 'react';
import { tv } from 'tailwind-variants';
import { Button } from '../../../ui-kit/button.mjs';
import { Truncate } from '../../Truncate/index.mjs';
import DataBuilderCondition from '../Condition/index.mjs';
import { makeSubField } from '../data-field.mjs';
import { DataBuilderFormField } from '../DataBuilderFormField.mjs';
import useDataBuilderField from '../use-data-builder-field.mjs';

const caseFormulaRecipe = tv({
  slots: withSlotScopeClasses("case-formula", {
    container: "mt-2",
    "add-button": "w-fit h-fit rounded-xl text-slate-11 px-2 py-1 my-2"
  })
});
const CaseFormula = forwardRef((props, ref) => {
  return /* @__PURE__ */ jsxs(EditorTagBadge, { ref, variant: "value", onDelete: props.onDelete, children: [
    /* @__PURE__ */ jsx(EditorTagBadgeIcon, { type: SvgIconType.Array }),
    /* @__PURE__ */ jsx(Truncate, { children: "Conditional value" })
  ] });
});
CaseFormula.displayName = "CaseFormula";
function CaseFormulaDropdown({ formula, onChange }) {
  const { field } = useDataBuilderField();
  const styles = caseFormulaRecipe();
  if (formula?.type !== DataBuilderFormulaType.CASE) {
    return /* @__PURE__ */ jsx("span", { children: "Unexpected value for Case drilldown. Something went wrong." });
  }
  const defaultValue = formula.value?.default;
  const cases = formula.value?.cases ?? [];
  function handleChange(value) {
    onChange?.({
      $case: value
    });
  }
  function handleCaseChange(index, value) {
    const newConditions = [...cases];
    newConditions[index] = value;
    handleChange({
      cases: newConditions,
      default: defaultValue
    });
  }
  function handleNewCase() {
    handleChange({
      cases: [...cases, { filter: [], value: null }],
      default: defaultValue
    });
  }
  function handleDeleteCase(idx) {
    const newConditions = [...cases];
    newConditions.splice(idx, 1);
    handleChange({
      cases: newConditions,
      default: defaultValue
    });
  }
  function handleDefaultValueChange(value) {
    handleChange({
      cases,
      default: value
    });
  }
  function makeCaseField(item, index) {
    return makeSubField(field, {
      valueLocator: `${field.valueLocator}.$case.cases[${index}]`,
      schema: {
        type: "object",
        properties: {
          value: schemaWithTitle(field.schema ?? {}, `Case ${index + 1}`)
        }
      },
      optionFactories: field.optionFactories,
      exampleVariables: field.exampleVariables,
      variablesSchema: field.variablesSchema,
      level: 1,
      /* FIXME: strictNullCheck temporary fix */
      // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string | un... Remove this comment to see the full error message
      key: null,
      value: item,
      isInSchema: true
    });
  }
  const defaultCaseField = makeSubField(field, {
    valueLocator: `${field.valueLocator}.$case.default`,
    schema: schemaWithTitle(field.schema ?? {}, `Default`),
    optionFactories: field.optionFactories,
    exampleVariables: field.exampleVariables,
    variablesSchema: field.variablesSchema,
    level: 1,
    /* FIXME: strictNullCheck temporary fix */
    // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string | un... Remove this comment to see the full error message
    key: null,
    value: defaultValue,
    isInSchema: true
  });
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    cases.map((item, index) => {
      const caseField = makeCaseField(item, index);
      return /* @__PURE__ */ jsx(
        CaseItem,
        {
          field: caseField,
          item,
          onChange: (value) => handleCaseChange(index, value),
          onDelete: () => handleDeleteCase(index)
        },
        index
      );
    }),
    /* @__PURE__ */ jsx(
      Button,
      {
        onClick: handleNewCase,
        variant: "outline",
        colorPalette: "gray",
        size: "sm",
        className: styles["add-button"](),
        children: "+ Add Case"
      }
    ),
    /* @__PURE__ */ jsx(DataBuilderFormField, { field: defaultCaseField, onChange: handleDefaultValueChange })
  ] });
}
function CaseItem({
  field,
  item,
  onChange,
  onDelete
}) {
  const styles = caseFormulaRecipe();
  function handleValueChange(value) {
    return onChange({
      ...item ?? {},
      value
    });
  }
  const filterField = makeSubField(field, {
    valueLocator: `${field.valueLocator}.filter`,
    key: "filter",
    value: item?.filter,
    schema: { type: "boolean" }
  });
  const valueField = makeSubField(field, {
    valueLocator: `${field.valueLocator}.value`,
    value: item?.value,
    isInSchema: true,
    schema: field.schema?.properties?.value,
    level: 1,
    isDeletable: true
  });
  function handleFilterChange(value) {
    return onChange({
      ...item ?? {},
      filter: value
    });
  }
  return /* @__PURE__ */ jsxs("div", { className: styles.container(), children: [
    /* @__PURE__ */ jsx(DataBuilderFormField, { field: valueField, onChange: handleValueChange, onDelete }),
    /* @__PURE__ */ jsx(DataBuilderCondition, { field: filterField, onChange: handleFilterChange })
  ] });
}

export { CaseFormulaDropdown, CaseFormula as default };
